<template>
  <div class="px-5 sm:px-6 lg:px-10 py-6">
    <div class="mb-6 flex justify-between items-center">
      <h1 class="text-[22px] sm:text-[24px] font-bold text-gray-900">
        {{ pageTitle }}
      </h1>

      <button
        v-if="isEditable && !loading"
        type="button"
        class="h-[38px] px-4 rounded-full font-bold text-white shadow-sm"
        :style="{ backgroundColor: '#5B9AC5' }"
        :disabled="submitting"
        @click="onSubmit"
      >
        {{ mode==='add' ? 'Add New Procurement' : 'Save' }}
      </button>

      <div v-if="loading && mode !== 'add'" class="h-[38px] w-28 rounded-full bg-gray-200 animate-pulse"></div>
    </div>

    <div v-if="loading" class="border border-gray-300 rounded-2xl bg-white">
      <div class="max-w-[900px] mx-auto p-6 space-y-5">
        <div class="h-10 w-full rounded-lg bg-gray-200 animate-pulse"></div>
        <div class="h-10 w-full rounded-lg bg-gray-200 animate-pulse"></div>
        <div class="h-10 w-full rounded-lg bg-gray-200 animate-pulse"></div>
        <div class="h-5 w-48 rounded bg-gray-200 animate-pulse mt-2"></div>
        <div class="grid grid-cols-2 gap-4">
          <div v-for="i in 10" :key="i" class="h-10 rounded-lg bg-gray-200 animate-pulse"></div>
          <div class="col-span-2 h-20 rounded-lg bg-gray-200 animate-pulse"></div>
        </div>
        <div class="flex justify-center mt-4">
          <div class="h-5 w-44 rounded bg-gray-200 animate-pulse"></div>
        </div>
      </div>
    </div>

    <div v-else class="border border-gray-300 rounded-2xl bg-white">
      <div class="max-w-[900px] mx-auto p-6">
        <DynamicForm
          ref="dynamicForm"
          :schema="formSchema"
          :mode="mode==='view' ? 'view' : 'edit'"
          :errors="formErrors"
          v-model="form"
          :showActions="false"
          @clearError="clearFormError"
        />

        <div v-if="isEditable" class="mt-4 flex justify-center">
          <label class="flex items-center gap-2 cursor-pointer text-sm text-gray-600">
            <div class="relative" @click="toggleManual">
              <div
                class="w-10 h-5 rounded-full"
                :class="manualInvoiceAmount ? 'bg-blue-500' : 'bg-gray-300'"
              ></div>
              <div
                class="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition"
                :class="manualInvoiceAmount ? 'translate-x-5' : ''"
              ></div>
            </div>
            Edit Invoice Amount manually
          </label>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/api";
import DynamicForm from "@/components/DynamicForm.vue";

export default {
  name: "ProcurementForm",
  components: { DynamicForm },

  data() {
    return {
      loading: false,
      submitting: false,
      _loadingData: false,

      mode: "add",
      id: null,
      manualInvoiceAmount: false,
      manualInvoiceValue: null,
      accountTreeOptions: [],
      vehicleOptions: [],
      supplierOptions: [],

      formErrors: {},

      form: {
        company_id: "",
        company_name: "",
        account_tree_id: "",
        vehicle_number: "",
        procurement_id: "",
        date: "",
        supplier_id: "",
        supplier_name: "",
        invoice_date: "",
        tax_number: "",
        invoice_number: "",
        total_amount: null,
        tax_percentage: "",
        tax_amount: null,
        invoice_amount_after_tax: null,
        invoice_details: "",
        insurance_expiry_date: "",
      },
    };
  },

  watch: {
    "form.total_amount"() {
      if (this._loadingData) return;
      this.recalcTax();
    },
    "form.tax_percentage"() {
      if (this._loadingData) return;
      this.recalcTax();
    },

    "form.supplier_id"(val) {
      const selected = this.supplierOptions.find(s => s.value == val);
      if (selected) {
        this.form.supplier_name = selected.label;
      }
    },
   "form.invoice_amount_after_tax"(val) {
      if (this._loadingData) return;
      if (!this.manualInvoiceAmount) return;

      const total = parseFloat(this.form.total_amount) || 0;
      const base = parseFloat(val) || 0;
      this.form.tax_amount = this.formatToThreeDecimals(total - base);
    },
    "form.account_tree_id"(val) {
        if (String(val) !== '97') {
          this.form.insurance_expiry_date = '';
        }
      },
  },

  computed: {
    authConfig() {
      const token = localStorage.getItem("token");
      return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
    },

    isEditable() {
      return this.mode === "add" || this.mode === "edit";
    },

    pageTitle() {
      if (this.mode === "add") return "Add Procurement";
      if (this.mode === "edit") return "Edit Procurement";
      return "View Procurement";
    },
    isInsuranceSelected() {
      const selected = this.accountTreeOptions.find(
        opt => opt.value == this.form.account_tree_id
      );
      return selected?.label === 'م. تأمين سيارات';
    },
    formSchema() {
      return {
        sections: [
          {
            cols: 1,
            fields: [
              {
                key: "company_name",
                label: "For Company",
                type: "text",
                disabledIf: () => true,
              },
            ],
          },

          {
            cols: 1,
            fields: [
              {
                key: "account_tree_id",
                label: "Account Tree",
                type: "select",
                options: this.accountTreeOptions,
                required: true,
              },
            ],
          },

          {
            cols: 1,
            fields: [
              {
                key: "vehicle_number",
                label: "Vehicle",
                type: "select",
                options: this.vehicleOptions,
              },
            ],
          },

          {
            title: "Procurement Details",
            cols: 2,
            fields: [
              {
                key: "procurement_id",
                label: "Procurement ID",
                type: "text",
                disabledIf: () => true,
              },

              {
                key: "date",
                label: "Date",
                type: "date",
                disabledIf: () => true,
              },
                { key: "invoice_date", label: "Invoice Date", type: "date", required: true },
              ...(this.isInsuranceSelected ? [{
                key: "insurance_expiry_date",
                label: "Insurance Expiry Date",
                type: "date",
                required: true,
              }] : []),
              {
                key: "supplier_id",
                label: "Supplier",
                type: "select",
                options: this.supplierOptions,
                required: true,
              },

              { key: "tax_number", label: "Tax Number", type: "text" },
              { key: "invoice_number", label: "Invoice Number", type: "text", required: true },

              { key: "total_amount", label: "Total Amount", type: "number", required: true },

              {
                key: "tax_percentage",
                label: "Tax",
                type: "select",
                options: ["0", "5", "10", "16"].map(x => ({
                  label: `${x}%`,
                  value: x,
                })),
              },

             {
                key: "tax_amount",
                label: "Tax Amount",
                type: "number",
                disabledIf: () => true,
                step: "0.001",
                format: (val) => val ? Number(val).toFixed(3) : "0.000",
              },
              {
                key: "invoice_amount_after_tax",
                label: "Invoice Amount Before Tax",
                type: "number",
                disabledIf: () => !this.manualInvoiceAmount,
                step: "0.001",
                format: (val) => val ? Number(val).toFixed(3) : "0.000",
              },

              {
                key: "invoice_details",
                label: "Invoice Details",
                type: "textarea",
                colSpan: 2,
              },
            ],
          },
        ],
      };
    },
  },

  async mounted() {
  const q  = this.$route?.query   || {}
  const st = window.history.state || {}

  this.mode = st.procMode ?? q.mode ?? 'add'
  this.id   = st.procId   ?? q.id   ?? null

  let companyId   = ''
  let companyName = ''

  if (this.mode === 'add') {
    companyId   = st.procCompanyId   || q.company_id   || ''
    companyName = st.procCompanyName || (q.company_name ? decodeURIComponent(q.company_name) : '') || ''

    if (!companyId)   companyId   = sessionStorage.getItem('procurement_selected_company_id')   || ''
    if (!companyName) companyName = sessionStorage.getItem('procurement_selected_company_name') || ''
  }

  if (companyId)   this.form.company_id   = String(companyId)
  if (companyName) this.form.company_name = companyName

  if (!this.form.date) {
    this.form.date = new Date().toISOString().slice(0, 10)
  }

  if (this.mode === 'add') {
    this.form.procurement_id = 'Auto-generated after saving'
  }

  this.loading = this.mode !== 'add'

  await this.fetchAccountTree()
  await this.fetchSuppliers()
  if (this.form.company_id) await this.fetchVehicles(this.form.company_id)

  if (this.mode !== 'add') {
    await this.loadData(this.id)
  }

  this.loading = false
},
  methods: {
    async fetchSuppliers() {
      try {
        const res = await api.get("/accounting/suppliers", this.authConfig);
        const data = res.data?.data || res.data;
        this.supplierOptions = data.map(s => ({
          label: s.supplier_name,
          value: s.id,
        }));
      } catch (e) {
        console.error(e);
      }
    },

     formatToThreeDecimals(value) {
        const num = Number(value);
        if (isNaN(num)) return '0.000';
        const precise = parseFloat(num.toFixed(10));
        const result = Math.floor(precise * 1000) / 1000;
        return result.toFixed(3);
      },

    toggleManual() {
      if (!this.manualInvoiceAmount) {
        this.manualInvoiceValue = this.form.invoice_amount_after_tax;
        this.manualInvoiceAmount = true;
      } else {
        this.manualInvoiceAmount = false;
        this.form.invoice_amount_after_tax = this.manualInvoiceValue;
        if (this.form.invoice_amount_after_tax) {
          this.form.invoice_amount_after_tax = this.formatToThreeDecimals(this.form.invoice_amount_after_tax);
        }
      }
    },

    recalcTax() {
      const total = parseFloat(this.form.total_amount) || 0;
      const taxPct = parseFloat(this.form.tax_percentage) || 0;

      if (!taxPct) {
        this.form.tax_amount = this.formatToThreeDecimals(0);
        if (!this.manualInvoiceAmount) {
          this.form.invoice_amount_after_tax = this.formatToThreeDecimals(total);
        }
        return;
      }

      const base = total / (1 + taxPct / 100);
      const taxAmt = total - base;

      this.form.tax_amount = this.formatToThreeDecimals(taxAmt);

      if (!this.manualInvoiceAmount) {
        this.form.invoice_amount_after_tax = this.formatToThreeDecimals(base);
      }
    },

    async fetchAccountTree() {
      try {
        const res = await api.get(
          "/accounting/purchasing-fund/account-tree-folders",
          this.authConfig
        );
        const data = res.data?.data || res.data;
        this.accountTreeOptions = data.map(item => ({
          label: item.name,
          value: item.id,
        }));
      } catch (e) {
        console.error(e);
      }
    },

    async fetchVehicles(companyId) {
      try {
        const res = await api.get("/accounting/purchasing-fund/vehicles", {
          ...this.authConfig,
          params: { company_id: companyId },
        });
        const data = res.data?.data || res.data;
        this.vehicleOptions = data.map(v => ({
          label: v.vehicle_number,
          value: v.vehicle_number,
        }));
      } catch (e) {
        console.error(e);
      }
    },

   async loadData(id) {
      try {
        const res = await api.get(`/accounting/procurements/${id}`, this.authConfig);
        const data = res.data?.data || res.data;

        const cleanNumber = (val) => {
          if (val === null || val === undefined || val === '') return null;
          return Number(val);
        };

        const cleanPercent = (val) => {
          if (val === null || val === undefined || val === '') return '';
          return String(parseInt(val));
        };

        const cleaned = {
          ...data,
          procurement_id: data.company_sequence ?? data.id,
          total_amount: cleanNumber(data.total_amount),
          tax_amount: data.tax_amount ? this.formatToThreeDecimals(data.tax_amount) : null,
          invoice_amount_after_tax: data.invoice_amount_after_tax ? this.formatToThreeDecimals(data.invoice_amount_after_tax) : null,
          tax_percentage: cleanPercent(data.tax_percentage),
        };

        this._loadingData = true;
        Object.assign(this.form, cleaned);

        if (data.company_name) {
          this.form.company_name = data.company_name;
        } else {
          this.form.company_name = sessionStorage.getItem('procurement_selected_company_name') || '';
        }

        if (data.company_id) {
          this.form.company_id = String(data.company_id);
        } else {
          this.form.company_id = sessionStorage.getItem('procurement_selected_company_id') || '';
        }

        if (!data.supplier_id && data.supplier_name) {
          const alreadyExists = this.supplierOptions.some(
            s => s.label === data.supplier_name
          );
          if (!alreadyExists) {
            this.supplierOptions = [
              { label: data.supplier_name, value: '__fallback__' },
              ...this.supplierOptions,
            ];
          }
          this.form.supplier_id = '__fallback__';
        }

        if (this.form.company_id) {
          await this.fetchVehicles(this.form.company_id);
        }

        await this.$nextTick();
        this._loadingData = false;

      } catch (e) {
        this._loadingData = false;
        console.error(e);
      }
    },

    clearFormError(key) {
      if (this.formErrors[key]) {
        const updated = { ...this.formErrors };
        delete updated[key];
        this.formErrors = updated;
      }
    },

    async onSubmit() {
      const requiredFields = [
        { key: "account_tree_id", label: "Account Tree" },
        { key: "supplier_id",     label: "Supplier" },
        { key: "invoice_date",    label: "Invoice Date" },
        { key: "invoice_number",  label: "Invoice Number" },
        { key: "total_amount",    label: "Total Amount" },
      ];
      if (this.isInsuranceSelected) {
                requiredFields.push({ key: "insurance_expiry_date", label: "Insurance Expiry Date" });
      }
      const errors = {};
      requiredFields.forEach(f => {
        const v = this.form[f.key];
        if (v === null || v === undefined || String(v).trim() === "") {
          errors[f.key] = `${f.label} is required`;
        }
      });

      if (Object.keys(errors).length) {
        this.formErrors = errors;
        const firstKey = Object.keys(errors)[0];
        this.$nextTick(() => {
          this.$refs.dynamicForm?.focusField(firstKey);
        });
        return;
      }

      this.formErrors = {};
      this.submitting = true;

      try {
        const body = {
              ...this.form,
              supplier_id: this.form.supplier_id === '__fallback__' ? null : this.form.supplier_id,
              insurance_expiry_date: this.isInsuranceSelected ? this.form.insurance_expiry_date : null,
            };
        let res;
        if (this.mode === "edit") {
          res = await api.put(`/accounting/procurements/${this.id}`, body);
        } else {
          res = await api.post(`/accounting/procurements`, body);
        }

        this.$router.push({ name: 'Procurement' });

      } catch (e) {
        const serverErrors = e?.response?.data?.errors;
        if (serverErrors && typeof serverErrors === 'object') {
          const mapped = {};
          Object.entries(serverErrors).forEach(([key, msgs]) => {
            mapped[key] = Array.isArray(msgs) ? msgs[0] : msgs;
          });
          this.formErrors = mapped;
          const firstKey = Object.keys(mapped)[0];
          this.$nextTick(() => {
            this.$refs.dynamicForm?.focusField(firstKey);
          });
        } else {
          alert(e?.response?.data?.message || e?.message || "Failed to save");
        }
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style scoped>
</style>
